import { Injectable, NgZone } from '@angular/core';
import { AppConfiguration } from '@symphony-solo/shared/app-configuration/src';
import { BarcodeScannedAction } from '../+state';
import { ScannerFacade } from '../+state/scanner.facade';
import { ScannerCallbacksBuilder } from '../builders';
import { ScannerSettingsProvider } from '../providers';
import { ScannerAdapterServiceBase } from './scanner-adapter.service.base';

@Injectable()
export class ScannerAdapterService extends ScannerAdapterServiceBase {
  private barcodePicker?: Scandit.BarcodePicker;
  private actionToDispatchOnBarcodeScanned: BarcodeScannedAction;

  constructor(
    private scannerSettingsProvider: ScannerSettingsProvider,
    private scannerFacade: ScannerFacade,
    private ngZone: NgZone,
    private appConfig: AppConfiguration
  ) {
    super();
    Scandit.License.setAppKey(this.appConfig.scanditLicense);
    this.initializeBarcodePicker();
    this.actionToDispatchOnBarcodeScanned = null;
  }

  public showScanner(actionToDispatchOnBarcodeScanned: BarcodeScannedAction): void {
    this.actionToDispatchOnBarcodeScanned = actionToDispatchOnBarcodeScanned;
    this.barcodePicker.setMargins(this.scannerSettingsProvider.getScannerMargins());

    const scanOverlay = this.barcodePicker.getOverlayView();
    scanOverlay.setGuiStyle(Scandit.ScanOverlay.GuiStyle.LASER);

    const callbacks = this.createCallbacks();
    this.barcodePicker.show(callbacks);
  }

  public closeScaner(): void {
    this.barcodePicker.cancel();
    this.actionToDispatchOnBarcodeScanned = null;
  }

  public startScanning(): void {
    this.barcodePicker.startScanning(true);
  }

  public stopScanning(): void {
    this.barcodePicker.stopScanning();
  }

  public resumeScanning(): void {
    this.barcodePicker.resumeScanning();
  }

  public pauseScanning(): void {
    this.barcodePicker.pauseScanning();
  }

  public changeMargins(): void {
    this.barcodePicker.setMargins(this.scannerSettingsProvider.getScannerMargins());
  }

  private initializeBarcodePicker(): void {
    const settings = this.scannerSettingsProvider.createSettings();
    this.barcodePicker = new Scandit.BarcodePicker(settings);
    this.barcodePicker.continuousMode = true;
    this.barcodePicker.getOverlayView().setVibrateEnabled(true);
    this.barcodePicker.getOverlayView().setBeepEnabled(true);
  }

  private createCallbacks(): Scandit.Callbacks {
    const scanditCallbacksBuilder = new ScannerCallbacksBuilder();
    const callbacks = scanditCallbacksBuilder
      .setDidScan(this.didScan.bind(this))
      .setDidCancel(this.didCancel.bind(this))
      .setDidChangeState(this.didChangeState.bind(this))
      .build();

    return callbacks;
  }

  private didScan(scanSession: Scandit.ScanSession): any {
    this.ngZone.run(() => {
      const barcodesScanned = scanSession.newlyRecognizedCodes;
      if (barcodesScanned.length !== 0) {
        const barcode = scanSession.newlyRecognizedCodes[0].data;
        this.scannerFacade.barcodeScanned(barcode);
        this.scannerFacade.dispathCustomActionOnBarcodeScanned(this.actionToDispatchOnBarcodeScanned, barcode);
      }
    });
  }

  private didCancel(reason: any): any {
    console.log('session of scanner finished ' + reason);
  }

  private didChangeState(newState: Scandit.BarcodePicker.State): any {
    this.scannerFacade.scannerStateChanged(newState);
  }
}
